'use client';

import type { Plan } from '@/app/actions/cart';
import {
  getLowestDiscountRate,
  getSavePct,
  hasDiscountForPeriod,
  PERIOD_TABS,
  SUBSCRIBE_PERIOD_LABEL,
} from '@/app/(public)/pricing/plan-utils';
import { cn } from '@/lib/utils';
import { motion } from 'motion/react';

interface PeriodTabsProps {
  plans: Plan[];
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
}

const PeriodTabs = ({
  plans,
  selectedPeriod,
  onPeriodChange,
}: PeriodTabsProps) => {
  return (
    <motion.div
      className="flex items-center justify-center mb-16"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.1, duration: 0.5 }}
    >
      <div className="inline-flex items-center bg-foreground/6 dark:bg-foreground/10 rounded-full p-1 gap-x-0.5">
        {PERIOD_TABS.map((period) => {
          const savePct = getSavePct(plans, period);
          const periodHasDiscount = hasDiscountForPeriod(plans, period);
          const discountRate = periodHasDiscount
            ? getLowestDiscountRate(plans, period)
            : null;
          const isActive = selectedPeriod === period;
          return (
            <button
              key={period}
              onClick={() => onPeriodChange(period)}
              className={cn(
                'relative flex flex-col md:flex-row items-center justify-center gap-1 md:gap-1.5 px-4 md:px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap',
                {
                  'bg-foreground text-background shadow-sm': isActive,
                  'text-muted-foreground hover:text-foreground': !isActive,
                },
              )}
            >
              <span>{SUBSCRIBE_PERIOD_LABEL[period]}</span>
              {periodHasDiscount && discountRate != null ? (
                <span
                  className={cn(
                    'text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap',
                    isActive
                      ? 'bg-background/20 text-background'
                      : 'bg-orange-100 dark:bg-orange-900/80 text-orange-600 dark:text-orange-300',
                  )}
                >
                  低至
                  {discountRate % 1 === 0
                    ? discountRate.toFixed(0)
                    : discountRate.toFixed(1)}
                  折
                </span>
              ) : savePct != null ? (
                <span
                  className={cn(
                    'text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap',
                    isActive
                      ? 'bg-background/20 text-background'
                      : 'bg-green-100 dark:bg-green-900/80 text-green-700 dark:text-green-300',
                  )}
                >
                  省{savePct}%
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default PeriodTabs;
